<template>
  <div class="floating-file-notes">
    <!-- Notes Panel -->
    <transition name="panel-expand">
      <div
        v-if="showNotesPanel"
        class="floating-file-notes__panel"
        @click.stop
      >
        <div class="panel__header">
          <div class="panel__title-container">
            <h3 class="panel__title">{{ currentTitle }}</h3>
            <div class="dropdown-container">
              <button
                class="panel__action-btn dropdown-toggle"
                @click.stop="toggleDropdown"
                title="Switch Note Type"
              >
                <span class="k-icon k-i-arrow-chevron-down"></span>
              </button>
              <transition name="dropdown-fade">
                <div v-if="showDropdown" class="dropdown-menu" @click.stop>
                  <button
                    v-if="selectedNoteType !== 'file'"
                    class="dropdown-item"
                    @click="selectNoteType('file')"
                  >
                    <span class="k-icon k-i-comment"></span>
                    File Notes
                  </button>
                  <button
                    v-if="selectedNoteType !== 'sales'"
                    class="dropdown-item"
                    @click="selectNoteType('sales')"
                  >
                    <span class="k-icon k-i-file-txt"></span>
                    Sales Notes
                  </button>
                </div>
              </transition>
            </div>
          </div>

          <div class="panel__header-actions">
            <button
              class="panel__action-btn"
              @click="openFullscreen"
              title="Open Fullscreen"
            >
              <span class="k-icon k-i-window"></span>
            </button>
            <button
              class="panel__close-btn"
              @click="toggleNotesPanel"
              title="Close"
            >
              <span class="k-icon k-i-x"></span>
            </button>
          </div>
        </div>
        <div class="panel__body">
          <loan-notes-component
            v-if="selectedNoteType === 'file'"
            :loan-id="loanId"
            :show-loan-notes="showNotesPanel"
          ></loan-notes-component>
          <sales-notes-component
            v-if="selectedNoteType === 'sales'"
            :loan-id="loanId"
            :show-sales-notes="showNotesPanel"
          ></sales-notes-component>
        </div>
      </div>
    </transition>

    <!-- Floating Button -->
    <button
      class="floating-file-notes__button"
      @click="toggleNotesPanel"
      :title="'Open ' + currentTitle"
    >
      <span class="k-icon" :class="selectedNoteType === 'file' ? 'k-i-comment' : 'k-i-file-txt'"></span>
      <span class="floating-file-notes__text">{{ currentTitle }}</span>
    </button>

    <!-- Click outside overlay -->
    <div
      v-if="showNotesPanel || showDropdown"
      class="floating-file-notes__overlay"
      @click="closeAll"
    ></div>

    <!-- Fullscreen Modals -->
    <loan-notes-dialog
      :showDialog="showFileNotesFullscreenModal"
      :loan-id="loanId"
      v-on:dialogclosed="toggleFileNotesFullscreenModal"
    ></loan-notes-dialog>

    <sales-notes-dialog
      :showDialog="showSalesNotesFullscreenModal"
      :loan-id="loanId"
      v-on:dialogclosed="toggleSalesNotesFullscreenModal"
    ></sales-notes-dialog>
  </div>
</template>

<script>
import loanNotesComponent from './loanNotesComponent.vue';
import loanNotesDialog from './loanNotesDialog.vue';
import salesNotesComponent from './salesNotesComponent.vue';
import salesNotesDialog from './salesNotesDialog.vue';

export default {
  name: 'FloatingFileNotesButton',
  components: {
    'loan-notes-component': loanNotesComponent,
    'loan-notes-dialog': loanNotesDialog,
    'sales-notes-component': salesNotesComponent,
    'sales-notes-dialog': salesNotesDialog
  },
  data() {
    return {
      showNotesPanel: false,
      showFileNotesFullscreenModal: false,
      showSalesNotesFullscreenModal: false,
      showDropdown: false,
      selectedNoteType: 'file' // 'file' or 'sales'
    };
  },
  computed: {
    loanId() {
      return this.$route.query.id;
    },
    currentTitle() {
      return this.selectedNoteType === 'file' ? 'File Notes' : 'Sales Notes';
    }
  },
  methods: {
    toggleNotesPanel() {
      this.showNotesPanel = !this.showNotesPanel;
      if (!this.showNotesPanel) {
        this.showDropdown = false;
      }
    },
    toggleFileNotesFullscreenModal() {
      this.showFileNotesFullscreenModal = !this.showFileNotesFullscreenModal;
    },
    toggleSalesNotesFullscreenModal() {
      this.showSalesNotesFullscreenModal = !this.showSalesNotesFullscreenModal;
    },
    openFullscreen() {
      this.showNotesPanel = false;
      if (this.selectedNoteType === 'file') {
        this.showFileNotesFullscreenModal = true;
      } else {
        this.showSalesNotesFullscreenModal = true;
      }
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    selectNoteType(type) {
      this.selectedNoteType = type;
      this.showDropdown = false;
    },
    closeAll() {
      this.showNotesPanel = false;
      this.showDropdown = false;
    }
  }
};
</script>

<style scoped>
.floating-file-notes {
  position: fixed;
  bottom: 15px;
  right: 20px;
  z-index: 999;
}

/* Overlay for click-outside-to-close */
.floating-file-notes__overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 998;
  background: transparent;
}

/* Notes Panel */
.floating-file-notes__panel {
  position: fixed;
  bottom: 70px;
  right: 20px;
  width: 420px;
  max-height: 80vh;
  background: var(--background);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  z-index: 1000;
  overflow: hidden;
}

.panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: var(--accent);
  color: var(--on-accent);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.panel__title-container {
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.panel__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.dropdown-container {
  position: relative;
}

.dropdown-toggle {
  padding: 4px 6px !important;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  overflow: hidden;
  z-index: 1001;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px;
  background: #ffffff !important;
  border: none;
  color: #333333 !important;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dropdown-item:hover {
  background: var(--accent) !important;
  color: var(--on-accent) !important;
}

.dropdown-item .k-icon {
  font-size: 16px;
}

.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.panel__header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.panel__action-btn,
.panel__close-btn {
  background: transparent;
  border: none;
  color: var(--on-accent);
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s ease;
}

  .panel__action-btn:hover,
  .panel__close-btn:hover {
    background: rgba(255, 255, 255, 0.2);
  }

.panel__action-btn .k-icon,
.panel__close-btn .k-icon {
  font-size: 18px;
}

.panel__body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 500px;
}

.panel-expand-enter-active {
  animation: panel-expand-in 0.3s ease-out;
}

.panel-expand-leave-active {
  animation: panel-expand-out 0.2s ease-in;
}

@keyframes panel-expand-in {
  from {
    opacity: 0;
    transform: scale(0.3);
    transform-origin: bottom right;
  }
  to {
    opacity: 1;
    transform: scale(1);
    transform-origin: bottom right;
  }
}

@keyframes panel-expand-out {
  from {
    opacity: 1;
    transform: scale(1);
    transform-origin: bottom right;
  }
  to {
    opacity: 0;
    transform: scale(0.3);
    transform-origin: bottom right;
  }
}

/* Floating Button */
.floating-file-notes__button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: var(--accent);
  color: var(--on-accent);
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  position: relative;
  z-index: 1001;
}

.floating-file-notes__button:hover {
  background: var(--accent-lt);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

.floating-file-notes__button:active {
  transform: translateY(0);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.floating-file-notes__button .k-icon {
  font-size: 18px;
}

.floating-file-notes__text {
  white-space: nowrap;
}
</style>

<style>
/* Non-scoped styles to override loan-notes-component within the panel */
.floating-file-notes__panel .loan-notes-component {
  height: 100% !important;
  max-height: 100% !important;
}

.floating-file-notes__panel .loan-notes-component .notes__container {
  height: 300px !important;
  max-height: 300px !important;
  overflow-y: scroll !important;
  overflow-x: hidden !important;
  -webkit-overflow-scrolling: touch;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar {
  width: 6px;
  display: block !important;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.floating-file-notes__panel .loan-notes-component .notes__container::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.floating-file-notes__panel .loan-notes-component .add-note__container {
  position: relative !important;
}
</style>
